import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE } from "../../../../config";

// use centralized API_BASE from config

// Turn any pasted URL into a storage object path (no domain, no bucket, no leading slash)
const toObjectPath = (value) => {
  if (!value) return value;
  let v = value.trim();

  // If bucket accidentally included, drop it (server uses a fixed bucket)
  v = v.replace(/^topic[_-]?videos?\//i, "");

  // Remove leading slashes
  v = v.replace(/^\/+/, "");
  return v;
};

// Admin component for managing topic videos
const AdminVideos = ({ topicId, onClose, onUpdate }) => {
  // Video state variables
  const [currentVideo, setCurrentVideo] = useState(null); // The video currently tied to the topic
  const [title, setTitle] = useState("");                 // Video title
  const [description, setDescription] = useState("");     // Video description
  const [filePath, setFilePath] = useState("");           // Path to the video in storage (object path)
  const [videoUrl, setVideoUrl] = useState("");           // Signed URL to preview the video
  const [level, setLevel] = useState("Foundation");       // Foundation or Higher
  const [isSaving, setIsSaving] = useState(false);        // Save button state
  const [loading, setLoading] = useState(true);           // Initial load

  // When the modal opens, fetch the video for the given topic
  useEffect(() => {
    const fetchVideo = async () => {
      try {
        const response = await axios.get(`${API_BASE}/api/videos`);
        const videos = (response.data || []).filter((v) => v.topic_id === topicId);

        if (videos.length === 0) {
          setCurrentVideo(null);
          setTitle("");
          setDescription("");
          setFilePath("");
          setLevel("Foundation");
          setVideoUrl("");
          return;
        }

        const video = videos[0]; // Assume one video per topic
        setCurrentVideo(video);
        setTitle(video.title || "");
        setDescription(video.description || "");
        setFilePath(video.file_path || "");
        setLevel(video.level || "Foundation");

        // If there's a file_path, fetch signed URL to preview the video
        if (video.file_path) {
          await fetchSignedVideoUrl(video.file_path);
        }
      } catch (err) {
        console.error("Error fetching video details:", err);
      } finally {
        setLoading(false);
      }
    };

    if (topicId) fetchVideo();
  }, [topicId]);

  // Get signed URL from backend to play the video from cloud storage
  const fetchSignedVideoUrl = async (path) => {
    const objectPath = toObjectPath(path);
    if (!objectPath) {
      setVideoUrl("");
      return;
    }

    try {
      const response = await axios.get(`${API_BASE}/api/videos/signed-url`, {
        params: { file_path: objectPath },
      });
      if (response.data.signedUrl) {
        setVideoUrl(response.data.signedUrl);
      } else {
        console.error("No signed URL received.");
        setVideoUrl("");
      }
    } catch (error) {
      console.error("Error fetching signed video URL:", error);
      setVideoUrl("");
    }
  };

  // Save video (either create new or update existing)
  const handleSaveVideo = async () => {
    // Validate required fields
    if (!title || !filePath || !level) {
      toast.error("Please fill in Title, File Path and Level.");
      return;
    }

    if (!window.confirm("Are you sure you want to save these changes?")) return;

    setIsSaving(true);
    try {
      const payload = {
        title,
        description,
        file_path: toObjectPath(filePath), // normalize to object path
        level,
        topic_id: topicId,
      };

      if (currentVideo?.id) {
        // Try PUT first (if your backend supports updates)
        try {
          await axios.put(`${API_BASE}/api/videos/${currentVideo.id}`, payload);
        } catch (putErr) {
          // Fallback: if PUT isn't available, POST a new row (prevents hard failure)
          if (
            putErr?.response?.status === 404 ||
            putErr?.response?.status === 405 ||
            putErr?.response?.status === 501
          ) {
            await axios.post(`${API_BASE}/api/videos`, payload);
          } else {
            throw putErr;
          }
        }
      } else {
        await axios.post(`${API_BASE}/api/videos`, payload);
      }

      toast.success("Video saved successfully.");

      // Refresh preview
      await fetchSignedVideoUrl(payload.file_path);

      if (typeof onUpdate === "function") onUpdate();
      onClose();
    } catch (err) {
      console.error("Error saving video:", err);
      toast.error(err.response?.data?.error || "Failed to save video. Please check the file path exists in your Supabase bucket.");
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md">
          <div className="text-teal-600">Loading…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 w-full md:w-3/4 lg:w-2/3 max-h-screen overflow-y-auto">
        <h2 className="text-2xl font-semibold text-teal-600 mb-4">Manage Video</h2>

        {/* Title input */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Title:</label>
          <input
            type="text"
            className="mt-1 block w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-teal-500 focus:border-teal-500"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        {/* Description input */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Description:</label>
          <textarea
            className="mt-1 block w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-teal-500 focus:border-teal-500"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        {/* File path input */}
        <div className="mb-2">
          <label className="block text-sm font-medium text-gray-700">
            File Path (inside <code>topic_videos</code>):
          </label>
          <input
            type="text"
            className="mt-1 block w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-teal-500 focus:border-teal-500"
            value={filePath}
            onChange={(e) => setFilePath(e.target.value)}
            placeholder="e.g. Grade1/Number/Addition/intro.mp4"
          />
        </div>
        <div className="flex items-center justify-between mb-4">
          <span className="text-xs text-gray-500">
            Stored as: <code>{toObjectPath(filePath) || "(none)"}</code>
          </span>
          <button
            onClick={() => fetchSignedVideoUrl(filePath)}
            className="text-teal-600 font-medium hover:underline"
          >
            Preview Video
          </button>
        </div>

        {/* Preview player */}
        {videoUrl && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">Preview:</label>
            <video controls className="w-full rounded-lg shadow">
              <source src={videoUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        )}

        {/* Level input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700">Level:</label>
          <select
            className="mt-1 block w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-teal-500 focus:border-teal-500"
            value={level}
            onChange={(e) => setLevel(e.target.value)}
          >
            <option value="Foundation">Foundation</option>
            <option value="Higher">Higher</option>
          </select>
        </div>

        {/* Action buttons */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            onClick={handleSaveVideo}
            className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600"
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminVideos;